import { useEffect, type RefObject } from "react";

/**
 * Toggles `data-scrolled` / `data-scrolled-end` on the table's scroll container
 * so CSS in `table.module.css` can render boundary shadows on sticky columns.
 */
export function useScrollShadows(tableRef: RefObject<HTMLTableElement | null>) {
  useEffect(() => {
    const container = tableRef.current?.parentElement;
    if (!container) return;

    const update = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      container.toggleAttribute("data-scrolled", container.scrollLeft > 0);
      container.toggleAttribute(
        "data-scrolled-end",
        container.scrollLeft < maxScroll - 1,
      );
    };

    update();
    container.addEventListener("scroll", update);

    // Recompute when the content or viewport width changes (async data load,
    // column visibility, window resize) — those don't fire `scroll` events, so
    // without this the boundary shadows stay stale until the user scrolls.
    const observer = new ResizeObserver(update);
    observer.observe(container);
    if (tableRef.current) observer.observe(tableRef.current);

    return () => {
      container.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [tableRef]);
}
